/**
 * Formats a date string or Date object into a localized date format.
 * @param dateInput Date string or Date object to format
 * @param locale Locale to use for formatting, defaults to 'ar-SA'
 * @returns Formatted date string
 */
export const formatDate = (dateInput: string | Date, locale: string = 'ar-SA'): string => {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  // Check if the date is valid
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
};

/**
 * Formats a date string or Date object into a localized date and time format.
 * @param dateInput Date string or Date object to format
 * @param locale Locale to use for formatting, defaults to 'ar-SA'
 * @returns Formatted date and time string
 */
export const formatDateTime = (dateInput: string | Date, locale: string = 'ar-SA'): string => {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  // Check if the date is valid
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleDateString(locale, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formats a date string or Date object into numeric date and time format.
 * @param dateInput Date string or Date object to format
 * @returns Formatted date and time string with numbers only (e.g., "2024/12/24 23:45")
 */
export const formatDateTimeNumeric = (dateInput: string | Date): string => {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  // Check if the date is valid
  if (isNaN(date.getTime())) return '';
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  
  return `${year}/${month}/${day} ${hours}:${minutes}`;
};

/**
 * Formats a date string or Date object into 'YYYY-MM-DD' format for form inputs.
 * @param dateInput Date string or Date object to format
 * @returns Formatted date string in 'YYYY-MM-DD' format
 */
export const formatDateForInput = (dateInput: string | Date): string => {
  if (!dateInput) return '';
  
  const date = typeof dateInput === 'string' ? new Date(dateInput) : dateInput;
  
  // Check if the date is valid
  if (isNaN(date.getTime())) return '';
  
  return date.toISOString().split('T')[0];
};